<template>
  <div>
    <b-modal
      id="modal-fleetPhoto-validation"
      centered
      size="lg"
      scrollable
      hide-header-close
      modal-class="mc-validasi-modal"
      body-class="mc-validasi-body"
    >
      <!-- ================= HEADER ================= -->
      <template #modal-header>
        <div class="mc-vhead">
          <div class="mc-vhead__title">
            {{ $t('task.column.fleetPhoto') }} :
            <strong>{{ licensePlate || 'No License Plate' }}</strong>
          </div>
          <div class="mc-vhead__meta">
            {{ workOrder.wo_number }}
            <span v-if="workOrder.customer_name"> · {{ workOrder.customer_name }}</span>
          </div>
          <b-button
            variant="link"
            class="mc-vhead__close"
            @click="$bvModal.hide('modal-fleetPhoto-validation')"
          >
            <i class="icon-outline_close" />
          </b-button>
        </div>
      </template>

      <!-- ================= RINGKASAN / PROGRESS ================= -->
      <div
        v-if="
          hasAccessAll([
            'sms-sop-validation:create',
            'sms-sop-validation:update',
            'sms-training-dataset:create',
            'sms-training-dataset:update',
          ])
        "
        class="mc-sticky-header"
      >
        <div class="mc-vsummary">
          <span class="mc-chip mc-chip--valid">
            <i class="icon-fill_checkmark-circle" /> {{ fleetValidCount }} valid
          </span>
          <span class="mc-chip mc-chip--invalid">
            <i class="icon-fill_close-circle" /> {{ fleetInvalidCount }} tidak valid
          </span>
          <span class="mc-chip mc-chip--muted">{{ fleetPendingCount }} belum</span>

          <div class="mc-vsummary__progress">
            <span class="mc-vsummary__label">
              {{ fleetValidCount + fleetInvalidCount }} dari
              {{ validatableFleetPhotoList.length }} foto divalidasi
            </span>
            <b-progress
              :value="fleetValidCount + fleetInvalidCount"
              :max="validatableFleetPhotoList.length || 1"
              height="6px"
              class="mc-vsummary__bar"
            />
          </div>
        </div>

        <div class="mc-vtype">
          <i class="icon-outline_car mc-vtype__icon" />
          <div>
            <div class="mc-vtype__caption">Tipe Kendaraan</div>
            <div class="mc-vtype__value">{{ vehicleTypeLabel }}</div>
          </div>
        </div>
      </div>

      <!-- ================= DAFTAR FOTO FLEET ================= -->
      <div
        v-if="fleetPhotoList.length === 0"
        style="padding: 20px; text-align: center; color: #999"
      >
        <p>Tidak ada foto kendaraan</p>
      </div>

      <div
        v-for="photo in fleetPhotoList"
        :key="photo.key"
        class="mc-photocard"
        :class="{
          'is-valid': fleetValidation[photo.key] === 'valid',
          'is-invalid': fleetValidation[photo.key] === 'invalid',
        }"
      >
        <!-- ===== TITLE ROW (SELALU DI ATAS FOTO) ===== -->
        <div class="mc-photocard__title-row">
          <div class="mc-photocard__name">{{ photo.label }}</div>
          <svg
            v-if="fleetValidation[photo.key] === 'valid'"
            class="mc-svg-icon mc-svg-icon--status"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" fill="#16a34a" />
            <path
              d="M8 12.5l2.5 2.5L16 9.5"
              stroke="#ffffff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
          <svg
            v-else-if="fleetValidation[photo.key] === 'invalid'"
            class="mc-svg-icon mc-svg-icon--status"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" fill="#dc2626" />
            <path
              d="M9 9l6 6M15 9l-6 6"
              stroke="#ffffff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
        </div>

        <!-- ===== AREA MEDIA / FOTO UTAMA ===== -->
        <div class="mc-photocard__media is-clickable" @click="openPreview(photo.src, photo.label)">
          <video
            v-if="isMediaVideo(photo.src)"
            :src="photo.src"
            class="mc-photocard__video"
            muted
          />
          <img v-else :src="photo.src" :alt="photo.label" />

          <button
            type="button"
            class="mc-photocard__zoom-btn"
            @click.stop="openPreview(photo.src, photo.label)"
          >
            <i :class="isMediaVideo(photo.src) ? 'icon-outline_play' : 'icon-outline_search'" />
          </button>
        </div>

        <!-- ===== FOOT CARD (AKSI & ALASAN) ===== -->
        <div class="mc-photocard__foot">
          <!-- TOMBOL VALIDASI -->
          <div
            v-if="
              isValidationRequired(photo) &&
              hasAccessAll([
                'sms-sop-validation:create',
                'sms-sop-validation:update',
                'sms-training-dataset:create',
                'sms-training-dataset:update',
              ])
            "
            class="mc-photocard__actions"
          >
            <b-button
              size="sm"
              pill
              :variant="fleetValidation[photo.key] === 'valid' ? 'success' : 'outline-success'"
              @click="setFleetValidation(photo.key, 'valid')"
            >
              <i class="icon-outline_checkmark-circle" /> Valid
            </b-button>
            <b-button
              size="sm"
              pill
              :variant="fleetValidation[photo.key] === 'invalid' ? 'danger' : 'outline-danger'"
              @click="setFleetValidation(photo.key, 'invalid')"
            >
              <i class="icon-outline_close-circle" /> Tidak valid
            </b-button>
          </div>

          <!-- SECTION ALASAN -->
          <div
            v-if="isValidationRequired(photo) && fleetValidation[photo.key] === 'invalid'"
            class="mc-photocard__reasons mt-3 p-3 rounded"
          >
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span class="mc-reasons__label font-weight-bold text-danger">
                Alasan tidak valid *
                <small class="text-muted font-weight-normal">(pilih 1 alasan)</small>
              </span>
              <span v-if="fleetInvalidReasons[photo.key]" class="mc-reasons__counter">
                1 alasan dipilih
              </span>
            </div>

            <div class="d-flex flex-wrap gap-2">
              <b-button
                v-for="reason in invalidReasonsList"
                :key="reason.invalid_reason_id"
                size="sm"
                pill
                :variant="
                  fleetInvalidReasons[photo.key] === reason.invalid_reason_id ? 'danger' : 'light'
                "
                class="mc-reason-chip"
                @click="selectReason(photo.key, reason.invalid_reason_id)"
              >
                {{ reason.name }}
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= FOOTER ================= -->
      <template #modal-footer>
        <div class="mc-vfoot">
          <div class="mc-vfoot__info">
            <span
              v-if="
                hasAccessAll([
                  'sms-sop-validation:create',
                  'sms-sop-validation:update',
                  'sms-training-dataset:create',
                  'sms-training-dataset:update',
                ])
              "
              class="mc-vfoot__validator"
            >
              Validator: <strong>{{ validatorName }}</strong>
            </span>
          </div>
          <div class="mc-vfoot__actions">
            <b-button
              pill
              variant="outline-secondary"
              @click="$bvModal.hide('modal-fleetPhoto-validation')"
            >
              {{ $t('global.close') }}
            </b-button>
            <b-button
              v-if="
                hasAccessAll([
                  'sms-sop-validation:create',
                  'sms-sop-validation:update',
                  'sms-training-dataset:create',
                  'sms-training-dataset:update',
                ])
              "
              pill
              variant="primary"
              :disabled="!canSaveFleetValidation"
              @click="onSaveValidation"
            >
              Simpan validasi
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-fleetPhoto-preview"
      centered
      size="xl"
      hide-footer
      hide-header
      modal-class="mc-preview-modal"
      @hidden="resetZoom"
    >
      <div
        class="mc-preview-stage"
        :class="{ 'is-draggable': zoomLevel > 1, 'is-dragging': isDragging }"
        @wheel.prevent="onPreviewWheel"
        @mousedown="onPreviewDragStart"
        @mousemove="onPreviewDragMove"
        @mouseup="onPreviewDragEnd"
        @mouseleave="onPreviewDragEnd"
        @dblclick="resetZoom"
      >
        <!-- Tombol close pojok kiri atas -->
        <button
          type="button"
          class="mc-preview-close"
          @click="$bvModal.hide('modal-fleetPhoto-preview')"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <!-- Judul pojok kiri atas (bawah close) -->
        <div class="mc-preview-title">{{ previewTitle }}</div>

        <!-- Media -->
        <video
          v-if="isPreviewVideo"
          :src="previewUrl"
          controls
          @click.stop
          style="pointer-events: auto"
          autoplay
          class="mc-preview-media"
        />
        <img
          v-else
          :src="previewUrl"
          :alt="previewTitle"
          class="mc-preview-media"
          :style="previewMediaStyle"
          draggable="false"
        />

        <!-- Floating zoom controls (pojok kanan bawah) -->
        <div class="mc-preview-zoom" @mousedown.stop @dblclick.stop>
          <button
            type="button"
            class="mc-preview-zoom__btn"
            :disabled="zoomLevel <= 0.5"
            @click="zoomOut"
          >
            −
          </button>
          <span class="mc-preview-zoom__value">{{ Math.round(zoomLevel * 100) }}%</span>
          <button
            type="button"
            class="mc-preview-zoom__btn"
            :disabled="zoomLevel >= 5"
            @click="zoomIn"
          >
            +
          </button>
          <button
            type="button"
            class="mc-preview-zoom__btn mc-preview-zoom__btn--reset"
            :disabled="zoomLevel === 1 && panX === 0 && panY === 0"
            @click="resetZoom"
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
            </svg>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

export default {
  name: 'ModalFleetPhotoValidation',

  props: {
    workOrder: {
      type: Object,
      default: () => ({}),
    },
    selectedTask: {
      type: [Object, String],
      default: () => ({}),
    },
    licensePlate: {
      type: String,
      default: '',
    },
    odometer: {
      type: [String],
      default: null,
    },
    imei: {
      type: [String],
      default: null,
    },
    phoneNo: {
      type: [String],
      default: null,
    },
    taskId: {
      type: [Number, String],
      default: null,
    },
    taskType: {
      type: String,
      default: 'I',
    },
    imeiPhoto: {
      type: [Boolean, String, Object],
      default: false,
    },
    imeiPhotoS3: {
      type: String,
      default: '',
    },
    licensePlatePhoto: {
      type: [Boolean, String, Object],
      default: false,
    },
    licensePlatePhotoS3: {
      type: String,
      default: '',
    },
    vehicleFullPhoto: {
      type: [Boolean, String, Object],
      default: false,
    },
    vehicleFullPhotoS3: {
      type: String,
      default: '',
    },
    odometerPhoto: {
      type: [Boolean, String, Object],
      default: false,
    },
    odometerPhotoS3: {
      type: String,
      default: '',
    },
    rearVehiclePhoto: {
      type: [Boolean, String, Object],
      default: false,
    },
    rearVehiclePhotoS3: {
      type: String,
      default: '',
    },
    invalidReasonsList: {
      type: Array,
      default: () => [],
    },
    existingValidations: {
      type: Array,
      default: () => [],
    },
    // Prop tambahan jika konfigurasi checklist diberikan langsung dari parent
    checklistConfigMap: {
      type: Object,
      default: () => ({}),
    },
  },

  mixins: [Helper],

  data() {
    return {
      fleetValidation: {},
      fleetInvalidReasons: {},
      fleetTrainingDatasetIds: {},
      previewUrl: '',
      previewTitle: '',

      // ZOOM STATE
      zoomLevel: 1,
      panX: 0,
      panY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
    }
  },

  computed: {
    fleetPhotoList() {
      const woType = this.workOrder.wo_type || this.workOrder.type
      const isInstall = woType === 'install'

      const task =
        typeof this.selectedTask === 'object' && this.selectedTask !== null ? this.selectedTask : {}

      const resolvePhotoUrl = (propBool, propS3, taskFile, taskPath) => {
        if (propS3) return propS3
        if (taskFile && taskFile.url) return taskFile.url
        if (typeof propBool === 'string' && propBool.startsWith('http')) return propBool
        if (propBool && typeof propBool === 'object' && propBool.url) return propBool.url
        if (taskPath) return taskPath
        return null
      }

      const getValidationRequiredFlag = (propObj, taskFile, defaultKey) => {
        if (propObj && typeof propObj === 'object' && 'is_validation_required' in propObj) {
          return propObj.is_validation_required
        }
        if (taskFile && typeof taskFile === 'object' && 'is_validation_required' in taskFile) {
          return taskFile.is_validation_required
        }
        if (this.checklistConfigMap && defaultKey in this.checklistConfigMap) {
          return this.checklistConfigMap[defaultKey]?.is_validation_required
        }
        return true
      }

      const imeiSrc = resolvePhotoUrl(
        this.imeiPhoto,
        this.imeiPhotoS3,
        task.imei_file,
        task.imei_photo
      )
      const lpSrc = resolvePhotoUrl(
        this.licensePlatePhoto,
        this.licensePlatePhotoS3,
        task.license_plate_file,
        task.license_plate_photo
      )
      const vfSrc = resolvePhotoUrl(
        this.vehicleFullPhoto,
        this.vehicleFullPhotoS3,
        task.vehicle_full_file,
        task.vehicle_full_photo
      )
      const odoSrc = resolvePhotoUrl(
        this.odometerPhoto,
        this.odometerPhotoS3,
        task.odometer_file,
        task.odometer_photo
      )
      const rearSrc = resolvePhotoUrl(
        this.rearVehiclePhoto,
        this.rearVehiclePhotoS3,
        task.rear_vehicle_file,
        task.rear_vehicle_photo
      )

      const list = [
        {
          key: 'imeiPhoto',
          relationTable: 'imei_file',
          label: isInstall ? this.$t('task.column.imeiPhoto') : this.$t('task.column.nopol'),
          src: imeiSrc,
          is_validation_required: getValidationRequiredFlag(
            this.imeiPhoto,
            task.imei_file,
            'imeiPhoto'
          ),
        },
        {
          key: 'licensePlatePhoto',
          relationTable: 'license_plate_file',
          label: isInstall
            ? this.$t('task.column.licensePlatePhoto')
            : this.$t('task.column.oldImei'),
          src: lpSrc,
          is_validation_required: getValidationRequiredFlag(
            this.licensePlatePhoto,
            task.license_plate_file,
            'licensePlatePhoto'
          ),
        },
        {
          key: 'vehicleFullPhoto',
          relationTable: 'vehicle_full_file',
          label: this.$t('task.column.vehicleFullPhoto'),
          src: vfSrc,
          is_validation_required: getValidationRequiredFlag(
            this.vehicleFullPhoto,
            task.vehicle_full_file,
            'vehicleFullPhoto'
          ),
        },
      ]

      const currentTaskType = this.taskType || task.tasks_type || 'I'
      if (currentTaskType !== 'A') {
        list.splice(2, 0, {
          key: 'odometerPhoto',
          relationTable: 'odometer_file',
          label: this.$t('task.column.odometerPhoto'),
          src: odoSrc,
          is_validation_required: getValidationRequiredFlag(
            this.odometerPhoto,
            task.odometer_file,
            'odometerPhoto'
          ),
        })
        list.push({
          key: 'rearVehiclePhoto',
          relationTable: 'rear_vehicle_file',
          label: this.$t('task.column.rearVehiclePhoto'),
          src: rearSrc,
          is_validation_required: getValidationRequiredFlag(
            this.rearVehiclePhoto,
            task.rear_vehicle_file,
            'rearVehiclePhoto'
          ),
        })
      }

      return list.filter((item) => Boolean(item.src))
    },

    validatableFleetPhotoList() {
      return this.fleetPhotoList.filter((photo) => this.isValidationRequired(photo))
    },

    fleetValidCount() {
      return this.validatableFleetPhotoList.filter((p) => this.fleetValidation[p.key] === 'valid')
        .length
    },

    fleetInvalidCount() {
      return this.validatableFleetPhotoList.filter((p) => this.fleetValidation[p.key] === 'invalid')
        .length
    },

    fleetPendingCount() {
      return this.validatableFleetPhotoList.length - this.fleetValidCount - this.fleetInvalidCount
    },

    vehicleTypeLabel() {
      const task =
        typeof this.selectedTask === 'object' && this.selectedTask !== null ? this.selectedTask : {}

      if (task.manufacturerModelType) return task.manufacturerModelType
      if (task.manufacturerModel) return task.manufacturerModel

      const getVal = (val) => {
        if (!val) return ''
        if (typeof val === 'string') return val.trim()
        if (typeof val === 'object') return val.name || val.label || val.title || ''
        return ''
      }

      const vt =
        task?.vehicleType ||
        task?.device?.vehicle?.vehicle_type ||
        task?.vehicle?.vehicle_type ||
        task?.vehicle_type ||
        task?.vehicle ||
        {}

      const manufacturer = getVal(vt.manufacturer || task.manufacturer)
      const model = getVal(vt.model_type || vt.model || task.model_type || task.model)
      const type = getVal(vt.type || task.type)

      const modelType = [model, type].filter(Boolean).join(' ')

      if (manufacturer && modelType) {
        return `${manufacturer} - ${modelType}`
      }

      return manufacturer || modelType || '-'
    },

    validatorName() {
      return this.getUserName()
    },

    isPreviewVideo() {
      return this.isMediaVideo(this.previewUrl)
    },

    canSaveFleetValidation() {
      if (this.fleetPendingCount > 0) return false

      const invalidPhotos = this.validatableFleetPhotoList.filter(
        (p) => this.fleetValidation[p.key] === 'invalid'
      )

      const allInvalidHaveReason = invalidPhotos.every((p) =>
        Boolean(this.fleetInvalidReasons[p.key])
      )

      return allInvalidHaveReason
    },

    bulkPayload() {
      const items = []

      this.validatableFleetPhotoList.forEach((photo) => {
        const fullUrl = photo.src
        if (!fullUrl) return
        const status = this.fleetValidation[photo.key]
        if (!status) return

        const url = this.extractPathFromUrl(fullUrl)
        const rawKey = photo.name || photo.key || ''
        const categoryName = rawKey.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()

        let name = this.vehicleTypeLabel
        if (categoryName=='license_plate_photo') name = this.licensePlate
        if (categoryName == 'odometer_photo') name = this.odometer
        if (categoryName=='imei_photo') name = this.imei + " - " + this.phoneNo

        const itemPayload = {
          name: name,
          category: categoryName,
          url: url,
          table_relation: 'task',
          relation_id: this.taskId,
          state: status,
          validate_at: new Date(),
          invalid_reason_id: status === 'invalid' ? this.fleetInvalidReasons[photo.key] : null,
          is_trained: false,
          trained_at: null,
          wo_id: this.workOrder.wo_id,
        }

        if (this.fleetTrainingDatasetIds[photo.key]) {
          itemPayload.training_dataset_id = this.fleetTrainingDatasetIds[photo.key]
        }

        items.push(itemPayload)
      })

      return items
    },

    previewMediaStyle() {
      return {
        transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`,
        transition: this.isDragging ? 'none' : 'transform 0.15s ease',
        cursor: this.zoomLevel > 1 ? (this.isDragging ? 'grabbing' : 'grab') : 'default',
      }
    },

    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },

  watch: {
    existingValidations: {
      immediate: true,
      handler(val) {
        this.fleetValidation = {}
        this.fleetInvalidReasons = {}
        this.fleetTrainingDatasetIds = {}

        if (!Array.isArray(val) || val.length === 0) return

        const categoryToKeyMap = {
          imei_photo: 'imeiPhoto',
          license_plate_photo: 'licensePlatePhoto',
          vehicle_full_photo: 'vehicleFullPhoto',
          odometer_photo: 'odometerPhoto',
          rear_vehicle_photo: 'rearVehiclePhoto',
          accu_photo: 'accuPhoto',
        }

        val.forEach((item) => {
          const photoKey = categoryToKeyMap[item.category]
          if (photoKey) {
            const statusVal = item.state || item.status
            if (statusVal) {
              this.$set(this.fleetValidation, photoKey, statusVal)
            }
            if (item.invalid_reason_id) {
              this.$set(this.fleetInvalidReasons, photoKey, item.invalid_reason_id)
            }
            if (item.training_dataset_id) {
              this.$set(this.fleetTrainingDatasetIds, photoKey, item.training_dataset_id)
            }
          }
        })
      },
    },
  },

  methods: {
    isValidationRequired(photo) {
      if (!photo) return false
      return photo.is_validation_required !== false
    },

    isMediaVideo(url) {
      if (!url) return false
      return url.includes('.mp4') || url.includes('.webm') || url.includes('.ogg')
    },

    openPreview(url, title) {
      if (!url) return
      this.resetZoom()
      this.previewUrl = url
      this.previewTitle = title || 'Preview Media'
      this.$bvModal.show('modal-fleetPhoto-preview') // ganti id sesuai file
    },

    // ===== ZOOM METHODS =====
    zoomIn() {
      this.zoomLevel = Math.min(+(this.zoomLevel + 0.25).toFixed(2), 5)
    },
    zoomOut() {
      this.zoomLevel = Math.max(+(this.zoomLevel - 0.25).toFixed(2), 0.5)
      if (this.zoomLevel <= 1) {
        this.panX = 0
        this.panY = 0
      }
    },
    resetZoom() {
      this.zoomLevel = 1
      this.panX = 0
      this.panY = 0
      this.isDragging = false
    },
    onPreviewWheel(e) {
      if (this.zoomLevel === 1 && e.deltaY > 0) return
      if (e.deltaY < 0) this.zoomIn()
      else this.zoomOut()
    },
    onPreviewDragStart(e) {
      if (this.zoomLevel <= 1 || e.button !== 0) return
      this.isDragging = true
      this.dragStartX = e.clientX - this.panX
      this.dragStartY = e.clientY - this.panY
    },
    onPreviewDragMove(e) {
      if (!this.isDragging) return
      this.panX = e.clientX - this.dragStartX
      this.panY = e.clientY - this.dragStartY
    },
    onPreviewDragEnd() {
      this.isDragging = false
    },

    setFleetValidation(key, value) {
      this.$set(this.fleetValidation, key, value)
      if (value !== 'invalid') {
        this.$set(this.fleetInvalidReasons, key, null)
      }
    },

    selectReason(photoKey, reasonId) {
      if (this.fleetInvalidReasons[photoKey] === reasonId) {
        this.$set(this.fleetInvalidReasons, photoKey, null)
      } else {
        this.$set(this.fleetInvalidReasons, photoKey, reasonId)
      }
    },

    onSaveValidation() {
      this.$emit('save', {
        items: this.bulkPayload,
      })
      this.$bvModal.hide('modal-fleetPhoto-validation')
    },

    resetValidation() {
      this.fleetValidation = {}
      this.fleetInvalidReasons = {}
      this.fleetTrainingDatasetIds = {}
      this.previewUrl = ''
      this.previewTitle = ''
    },

    extractPathFromUrl(url) {
      if (!url) return null
      try {
        const urlObj = new URL(url)
        let pathname = urlObj.pathname
        if (pathname.startsWith('/')) pathname = pathname.substring(1)
        return pathname
      } catch (e) {
        const match = url.match(/\/(upload\/[^?]+)/)
        return match ? match[1] : url
      }
    },
  },
}
</script>

<style lang="scss">
/* ---------- Modal shell ---------- */
.mc-validasi-modal .modal-content {
  border: 0;
  border-radius: 12px;
  overflow: hidden;
}
.mc-validasi-modal .modal-header {
  background: #123a86;
  color: #fff;
  padding: 0;
  border: 0;
}
.mc-validasi-body {
  background: #f6f8fb;
  padding: 16px 20px;
}

/* ---------- Header ---------- */
.mc-vhead {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}
.mc-vhead__title {
  font-size: 16px;
}
.mc-vhead__meta {
  margin-left: auto;
  font-size: 13px;
  opacity: 0.85;
  text-align: right;
}
.mc-vhead__close {
  color: #fff !important;
  padding: 0 4px;
  font-size: 20px;
}

/* ---------- Summary ---------- */
.mc-vsummary {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.mc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
}
.mc-chip--valid {
  color: #0f7a4d;
  background: #e6f6ee;
}
.mc-chip--invalid {
  color: #c0392b;
  background: #fdecea;
}
.mc-chip--muted {
  color: #6b7280;
  background: #f1f3f7;
}
.mc-vsummary__progress {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 240px;
}
.mc-vsummary__label {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}
.mc-vsummary__bar {
  flex: 1;
  background: #e5e9f2;
}

/* ---------- Tipe kendaraan ---------- */
.mc-vtype__icon {
  font-size: 22px;
  color: #123a86;
}
.mc-vtype__caption {
  font-size: 12px;
  color: #6b7280;
}
.mc-vtype__value {
  font-size: 15px;
  font-weight: 700;
  color: #0f2a5c;
}
.mc-vtype__actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

/* ---------- Kartu foto utama ---------- */
.mc-photocard {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
  transition: border-color 0.15s ease;
}
.mc-photocard.is-valid {
  border-color: #22a06b;
}
.mc-photocard.is-invalid {
  border-color: #e05a4d;
}
.mc-photocard__media {
  position: relative;
  background: #e6e9f0;
  border-radius: 8px;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &.is-clickable {
    cursor: pointer;
  }
}
.mc-photocard__media img,
.mc-photocard__video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.mc-photocard__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #8a93a5;
  font-size: 13px;
}
.mc-photocard__empty i {
  font-size: 28px;
}

/* ---------- Footer ---------- */
.mc-validasi-modal .modal-footer {
  background: #fff;
  border-top: 1px solid #e5e9f2;
}

.mc-vfoot__info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #6b7280;
}
.mc-vfoot__badge {
  border: 1px solid #e5e9f2;
}
.mc-vfoot__actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

/* ---------- Modal Preview Media (Lightbox) ---------- */
.mc-preview-modal .modal-content {
  border-radius: 12px;
  overflow: hidden;
  border: none;
}

/* --- Styling Tombol & Icon Styling --- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 500;
  transition: all 0.2s ease-in-out;

  [class^='icon-']:before,
  [class*=' icon-']:before {
    color: inherit !important;
  }
}

/* --- Variant Outline Success --- */
.btn-outline-success {
  color: #16a34a !important;
  border-color: #16a34a !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #16a34a !important;
    border-color: #16a34a !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(22, 163, 74, 0.25);
  }
}

/* --- Variant Outline Danger --- */
.btn-outline-danger {
  color: #dc2626 !important;
  border-color: #dc2626 !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #dc2626 !important;
    border-color: #dc2626 !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.25);
  }
}

/* --- Variant Outline Secondary --- */
.btn-outline-secondary {
  color: #4b5563 !important;
  border-color: #d1d5db !important;
  background-color: #ffffff;

  &:hover,
  &:active,
  &.active {
    background-color: #f3f4f6 !important;
    border-color: #9ca3af !important;
    color: #1f2937 !important;
  }
}

.mc-vfoot {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ---------- Kartu Foot Layout (Baris Bertumpuk) ---------- */
.mc-photocard__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px; /* jarak ke foto di bawahnya */
}

.mc-photocard__name {
  font-weight: 700;
  font-size: 14px;
  color: #0f2a5c;
}

.mc-photocard__foot {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px; /* tetap seperti sebelumnya */
}

.mc-photocard__status-icon {
  font-size: 18px;
}

.mc-photocard__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

/* Button Primary Navy Custom Styling */
.btn-primary {
  background-color: #123a86 !important;
  border-color: #123a86 !important;
  color: #ffffff !important;
}

.btn-outline-primary {
  color: #123a86 !important;
  border-color: #123a86 !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #123a86 !important;
    border-color: #123a86 !important;
    color: #ffffff !important;
  }
}

/* ---------- Tombol Zoom (Background Abu-abu & Icon Putih) ---------- */
.mc-photocard__zoom-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(107, 114, 128, 0.85);
  color: #ffffff !important;
  border: none;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  i {
    color: #ffffff !important;
  }

  &:hover {
    background: #4b5563;
    transform: scale(1.08);
  }

  &--sm {
    top: 6px;
    right: 6px;
    width: 26px;
    height: 26px;
    font-size: 11px;
  }
}

/* ---------- Container & Chip Alasan Tidak Valid ---------- */
.mc-photocard__reasons {
  background-color: #fdecea;
  border: 1px solid #f5c6cb;
}

.mc-reasons__label {
  font-size: 13px;
}

/* Teks counter "1 alasan dipilih" warna abu-abu */
.mc-reasons__counter {
  font-size: 12px;
  color: #6b7280 !important; /* Warna abu-abu */
  font-weight: 500;
}

.mc-reason-chip {
  font-size: 12px !important;
  font-weight: 500;
  border-radius: 999px;
  padding: 4px 12px;

  &.btn-light {
    background-color: #ffffff !important;
    border: 1px solid #e5e7eb !important;
    color: #374151 !important;

    &:hover {
      background-color: #f3f4f6 !important;
      border-color: #d1d5db !important;
    }
  }

  &.btn-danger {
    background-color: #e02424 !important;
    border-color: #e02424 !important;
    color: #ffffff !important;
    box-shadow: 0 2px 4px rgba(224, 36, 36, 0.2);
  }
}

.mc-vtype {
  position: sticky; /* Tambahkan ini agar tidak ikut ter-scroll */
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #eef4ff;
  border: 1px solid #dbe6fb;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
}

.mc-sticky-header {
  position: sticky;
  top: -16px;
  z-index: 10;
  background: #f6f8fb; /* Menyamakan warna latar modal agar menutup sempurna */
  padding-top: 16px;
  padding-bottom: 4px;
  margin-top: -16px;
}

.mc-svg-icon {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}
.mc-svg-icon--status {
  width: 20px;
  height: 20px;
}
.mc-svg-icon--btn {
  width: 14px;
  height: 14px;
}

/* ---------- Preview Modal (Lightbox) ---------- */
.mc-preview-modal .modal-content {
  background: #0f172a; /* gelap biar gambar standout */
  border: none;
  border-radius: 12px;
  overflow: hidden;
}
.mc-preview-modal .modal-body {
  padding: 0;
}

/* Stage full-bleed */
.mc-preview-stage {
  position: relative;
  width: 100%;
  height: 78vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
  user-select: none;
  touch-action: none;

  &.is-draggable {
    cursor: grab;
  }
  &.is-dragging {
    cursor: grabbing;
  }
}

.mc-preview-media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center center;
  will-change: transform;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none; /* biar drag di stage, bukan di img */
}

/* ---------- Tombol close pojok kiri atas ---------- */
.mc-preview-close {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 20;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background 0.15s ease;

  svg {
    display: block;
    stroke: currentColor;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.24);
    color: #fff;
  }
}

.mc-preview-title {
  position: absolute;
  top: 12px;
  left: 58px;
  z-index: 20;
  max-width: 60%;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(6px);
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  height: 36px;
  box-sizing: border-box;
}

.mc-preview-zoom {
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: rgba(17, 24, 39, 0.72);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  opacity: 0.35;
  transition: opacity 0.2s ease, transform 0.2s ease;

  /* Muncul lebih tegas saat hover stage */
  &:hover {
    opacity: 1;
  }
}

.mc-preview-stage:hover .mc-preview-zoom {
  opacity: 1;
}

.mc-preview-zoom__btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;

  svg {
    display: block;
    stroke: currentColor;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &--reset {
    font-size: 13px;
    margin-left: 2px;
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0;
    width: 36px;
  }
}

.mc-preview-zoom__value {
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  padding: 0 2px;
  user-select: none;
}

@media (hover: none) {
  .mc-preview-zoom {
    opacity: 1;
  }
}
</style>
